<template>
    <main class="p-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <div v-for="(stat, index) in stats" :key="index" class="bg-white rounded-lg shadow-md p-6 flex items-center">
          <div :class="`rounded-full p-3 ${stat.bgColor}`">
            <component :is="stat.icon" size="24" class="text-white" />
          </div>
          <div class="ml-4">
            <h3 class="text-lg font-semibold text-gray-700">{{ stat.title }}</h3>
            <p class="text-2xl font-bold text-gray-900">{{ stat.value }}</p>
          </div>
        </div>
      </div>
  
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div class="bg-white rounded-lg shadow-md p-6">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">Monthly Revenue</h3>
          <div class="h-64">
            <!-- 여기에 차트 컴포넌트를 추가할 수 있습니다 -->
            <div class="w-full h-full bg-gray-200 rounded flex items-center justify-center">
              Chart Placeholder
            </div>
          </div>
        </div>
        <div class="bg-white rounded-lg shadow-md p-6">
          <h3 class="text-lg font-semibold text-gray-700 mb-4">Recent Activities</h3>
          <ul class="space-y-4">
            <li v-for="(activity, index) in recentActivities" :key="index" class="flex items-center">
              <div :class="`rounded-full p-2 ${activity.bgColor}`">
                <component :is="activity.icon" size="16" class="text-white" />
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-gray-900">{{ activity.title }}</p>
                <p class="text-xs text-gray-500">{{ activity.time }}</p>
              </div>
            </li>
          </ul>
        </div>
      </div>
  
      <div class="bg-white rounded-lg shadow-md p-6">
        <h3 class="text-lg font-semibold text-gray-700 mb-4">Recent Orders</h3>
        <div class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-200">
            <thead class="bg-gray-50">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="order in recentOrders" :key="order.id">
                <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">#{{ order.id }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ order.customer }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ order.product }}</td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">${{ order.total }}</td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span :class="`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${order.statusColor}`">
                    {{ order.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </main>
  </template>
  
  <script setup>
  import {
    DollarSignIcon,
    ShoppingCartIcon,
    UserPlusIcon,
    TrendingUpIcon,
    BellIcon
  } from 'lucide-vue-next'
  
  const stats = [
    { title: 'Total Revenue', value: '$54,375', icon: DollarSignIcon, bgColor: 'bg-green-500' },
    { title: 'Total Orders', value: '1,472', icon: ShoppingCartIcon, bgColor: 'bg-blue-500' },
    { title: 'New Customers', value: '247', icon: UserPlusIcon, bgColor: 'bg-yellow-500' },
    { title: 'Growth', value: '15.3%', icon: TrendingUpIcon, bgColor: 'bg-purple-500' }
  ]
  
  const recentActivities = [
    { title: 'New order received', time: '2 minutes ago', icon: ShoppingCartIcon, bgColor: 'bg-blue-500' },
    { title: 'New user registered', time: '10 minutes ago', icon: UserPlusIcon, bgColor: 'bg-green-500' },
    { title: 'Product out of stock', time: '1 hour ago', icon: BellIcon, bgColor: 'bg-red-500' },
    { title: 'Payment received', time: '3 hours ago', icon: DollarSignIcon, bgColor: 'bg-yellow-500' }
  ]
  
  const recentOrders = [
    { id: '1234', customer: 'John Doe', product: 'Product A', total: '99.99', status: 'Completed', statusColor: 'bg-green-100 text-green-800' },
    { id: '1235', customer: 'Jane Smith', product: 'Product B', total: '149.99', status: 'Processing', statusColor: 'bg-yellow-100 text-yellow-800' },
    { id: '1236', customer: 'Bob Johnson', product: 'Product C', total: '199.99', status: 'Shipped', statusColor: 'bg-blue-100 text-blue-800' },
    { id: '1237', customer: 'Alice Brown', product: 'Product D', total: '79.99', status: 'Pending', statusColor: 'bg-orange-100 text-orange-800' },
    { id: '1238', customer: 'Charlie Wilson', product: 'Product E', total: '129.99', status: 'Completed', statusColor: 'bg-green-100 text-green-800' }
  ]
  </script>
  
  <style>
  /* Custom color for Tailwind */
  :root {
    --color-blue-700: #0066B3;
    --color-blue-800: #005A9E;
    --color-blue-900: #004F8A;
  }
  
  /* Override Tailwind classes */
  .bg-blue-700 {
    background-color: var(--color-blue-700);
  }
  
  .bg-blue-800 {
    background-color: var(--color-blue-800);
  }
  
  .bg-blue-900 {
    background-color: var(--color-blue-900);
  }
  
  .text-blue-700 {
    color: var(--color-blue-700);
  }
  
  .from-blue-700 {
    --tw-gradient-from: var(--color-blue-700);
    --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to, rgba(0, 102, 179, 0));
  }
  
  .to-blue-900 {
    --tw-gradient-to: var(--color-blue-900);
  }
  
  .hover\:bg-blue-800:hover {
    background-color: var(--color-blue-800);
  }
  </style>